import { DashboardMetrics } from '@/components/dashboard/dashboard-metrics';
import { HomeSummaryDialog } from '@/components/dashboard/home-summary-dialog';
import { AgendaSummary } from '@/components/dashboard/summaries/agenda-summary';
import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { EnrollmentsCard } from '@/components/enrollmentsCard';
import { ExpensesMonthCard } from '@/components/expenses/expenses-month-card';
import { PendingRecurrenceBanner } from '@/components/schedule/pending-recurrence-banner';
import { ServicesCard } from '@/components/servicesCard';
import { useProviderProfile } from '@/hooks/use-provider';
import { useProviderPendingRecurrences } from '@/hooks/use-recurrences';
import { useTerms } from '@/hooks/use-terms';
import { ThemedText } from '@/components/themed-text';
import { useMarketingMockAuth } from '@/lib/marketing-mock/use-marketing-mock-auth';
import { isMarketingMockEnabled } from '@/lib/marketing-mock/is-marketing-mock-enabled';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { router } from 'expo-router';
import { useAuthStore } from '@/store/authStore';
import { planTierOf } from '@/utils/plan-access';
import { trialDaysRemaining } from '@/utils/trial';
import { PressableScale } from '@/components/ui/pressable-scale';
import { FadeInView } from '@/components/ui/fade-in-view';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useIconChip } from '@/hooks/use-theme-colors';

function ScheduleShortcut() {
  const chip = useIconChip();
  const [open, setOpen] = React.useState(false);
  const terms = useTerms();

  return (
    <>
      <PressableScale
        haptic
        onPress={() => setOpen(true)}
        className="flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
        style={{
          borderCurve: 'continuous',
          boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 6px 16px rgba(23, 17, 10, 0.06)',
        }}
      >
        <View
          className="h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: chip('#FD5001', '1A').bg, borderCurve: 'continuous' }}
        >
          <Ionicons name="calendar-outline" size={22} color={chip('#FD5001', '1A').fg} />
        </View>
        <View className="flex-1">
          <ThemedText className="text-base font-bold text-neutral-900">Agenda</ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            {`Seus horários, ${terms.appointmentPlural} e encaixes`}
          </ThemedText>
        </View>
        <Ionicons className="text-neutral-400" name="chevron-forward" size={20} />
      </PressableScale>

      <HomeSummaryDialog
        open={open}
        onClose={() => setOpen(false)}
        icon="calendar-outline"
        title="Agenda"
        subtitle="Próximos 7 dias"
        seeMoreLabel="Ver agenda completa"
        onSeeMore={() => router.push('/(tabs)/(provider)/schedule')}
      >
        <AgendaSummary />
      </HomeSummaryDialog>
    </>
  );
}

function TrialActiveBanner({ daysLeft }: { daysLeft: number }) {
  return (
    <LinearGradient
      colors={['#FD5001', '#E64500']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{
        borderRadius: 16,
        borderCurve: 'continuous',
        boxShadow: '0 6px 16px rgba(253, 80, 1, 0.25)',
      }}
    >
      <View className="flex-row items-center gap-3 p-4">
        <View className="h-9 w-9 items-center justify-center rounded-full bg-white/15">
          <Ionicons name="gift-outline" size={18} color="#fff" />
        </View>
        <ThemedText className="flex-1 text-base font-bold text-white">Teste grátis ativo</ThemedText>
        <View className="rounded-full bg-white/20 px-3 py-1">
          <ThemedText className="text-xs font-bold text-white">
            {daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}
          </ThemedText>
        </View>
      </View>
    </LinearGradient>
  );
}

/**
 * Estado estrutural da conta (fica enquanto ela estiver no gratuito): card
 * igual aos vizinhos, sem gradiente — informa, não alarma.
 */
function FreePlanCard() {
  const chip = useIconChip();
  return (
    <PressableScale
      haptic
      onPress={() =>
        void navigateToSubscriptionPlans({ push: (path) => router.push(path as never) })
      }
      className="flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{
        borderCurve: 'continuous',
        boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 6px 16px rgba(23, 17, 10, 0.06)',
      }}
    >
      <View
        className="h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: chip('#FD5001', '1A').bg, borderCurve: 'continuous' }}
      >
        <Ionicons name="rocket-outline" size={22} color={chip('#FD5001', '1A').fg} />
      </View>
      <View className="flex-1">
        <ThemedText className="text-base font-bold text-neutral-900">Plano gratuito</ThemedText>
        <ThemedText className="text-xs text-neutral-500">
          Assine para ter WhatsApp automático, perfil público e assinaturas
        </ThemedText>
      </View>
      <Ionicons className="text-neutral-400" name="chevron-forward" size={20} />
    </PressableScale>
  );
}

export default function ProviderHomeScreen() {
  const { data: provider } = useProviderProfile();
  const { data: pendingRecurrences } = useProviderPendingRecurrences();
  useMarketingMockAuth();

  const user = useAuthStore((s) => s.user);
  const tier = isMarketingMockEnabled() ? null : planTierOf(user);
  const trialDaysLeft = trialDaysRemaining(provider?.trialEndsAt ?? null);

  const sections = [
    tier === 'TRIAL' ? <TrialActiveBanner key="trial" daysLeft={trialDaysLeft} /> : null,
    tier === 'FREE' ? <FreePlanCard key="free" /> : null,
    // Pedido de recorrência entra acima das métricas: precisa de resposta, não de leitura.
    (pendingRecurrences?.length ?? 0) > 0 ? (
      <PendingRecurrenceBanner key="pending-recurrences" />
    ) : null,
    <DashboardMetrics key="metrics" />,
    <ScheduleShortcut key="schedule" />,
    <ExpensesMonthCard key="expenses" />,
    <ServicesCard key="services" />,
    <EnrollmentsCard key="enrollments" />,
  ].filter(Boolean) as React.ReactElement[];

  return (
    <View className="flex-1 bg-canvas">
      <DashboardScrollView
        className="w-full bg-canvas"
        contentContainerStyle={{ paddingBottom: 80, flexGrow: 0 }}
      >
        <View className="mx-auto w-full max-w-6xl gap-4 p-5">
          {sections.map((section, index) => (
            <FadeInView key={section.key} delay={index * 80} className="w-full">
              {section}
            </FadeInView>
          ))}
        </View>
      </DashboardScrollView>
    </View>
  );
}
